import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

const requirements: PasswordRequirement[] = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'One number', test: (p) => /[0-9]/.test(p) },
  { label: 'One special character', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
];

interface PasswordStrengthProps {
  password: string;
}

export function getPasswordStrength(password: string): number {
  if (!password) return 0;
  const passed = requirements.filter((r) => r.test(password)).length;
  return passed;
}

export function isPasswordValid(password: string): boolean {
  return getPasswordStrength(password) === requirements.length;
}

export function PasswordStrength({ password }: PasswordStrengthProps) {
  const strength = getPasswordStrength(password);
  const maxStrength = requirements.length;

  const strengthLabels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
  const strengthColors = [
    'bg-destructive',
    'bg-warning',
    'bg-rating-good',
    'bg-success',
    'bg-primary',
  ];

  const labelIndex = Math.min(strength - 1, strengthLabels.length - 1);
  const label = strength > 0 ? strengthLabels[labelIndex] : '';
  const barColor = strength > 0 ? strengthColors[labelIndex] : 'bg-muted';

  return (
    <div className="space-y-2">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex gap-1 h-1.5">
          {Array.from({ length: maxStrength }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'flex-1 rounded-full transition-all duration-300',
                i < strength ? barColor : 'bg-muted'
              )}
            />
          ))}
        </div>
        {password.length > 0 && (
          <p
            className={cn(
              'text-xs font-medium transition-colors',
              strength === maxStrength ? 'text-primary' : 'text-muted-foreground'
            )}
          >
            {label}
          </p>
        )}
      </div>

      {/* Requirements checklist */}
      <ul className="space-y-1">
        {requirements.map((req) => {
          const passed = req.test(password);
          return (
            <li key={req.label} className="flex items-center gap-2 text-xs">
              {passed ? (
                <Check className="h-3 w-3 text-primary shrink-0" />
              ) : (
                <X className="h-3 w-3 text-muted-foreground shrink-0" />
              )}
              <span
                className={cn(
                  'transition-colors',
                  passed ? 'text-primary' : 'text-muted-foreground'
                )}
              >
                {req.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
